import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Building2, Briefcase, ShieldCheck, Map } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { t } from '../translations/translations';

const Fit = () => {
  const { language } = useLanguage();

  const routes = [
    {
      icon: Building2,
      plan: 'Incorporate',
      bestForKey: 'row1BestFor',
      valueKey: 'row1Value'
    },
    {
      icon: Briefcase,
      plan: 'Full Launch',
      bestForKey: 'row2BestFor',
      valueKey: 'row2Value'
    },
    {
      icon: ShieldCheck,
      plan: 'Corporate Compliance',
      bestForKey: 'row3BestFor',
      valueKey: 'row3Value'
    },
    {
      icon: Map,
      plan: t(language, 'fitPage.row4Plan'),
      bestForKey: 'row4BestFor',
      valueKey: 'row4Value'
    }
  ];

  const choices = ['choose1', 'choose2', 'choose3', 'choose4'];

  return (
    <div className="min-h-screen bg-[#fefdf4]" data-testid="fit-page">
      {/* Hero */}
      <section className="bg-[#fefdf4] py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-medium text-gray-900 mb-6 leading-tight">
            {t(language, 'fitPage.heroTitle')}
          </h1>
          <p className="text-xl text-gray-600 leading-relaxed">
            {t(language, 'fitPage.heroSubtitle')}
          </p>
          <div className="mt-8 flex justify-center">
            <Link to="/start">
              <Button
                size="lg"
                data-testid="fit-hero-begin-incorporation-btn"
                className="shine-cta bg-blue-900 hover:bg-blue-800 text-white px-8"
              >
                {t(language, 'nav.startCta')}
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Comparison Section */}
      <section className="py-20 bg-[#fefdf4]" data-testid="fit-comparison-section">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <h2 className="text-3xl md:text-4xl font-medium text-gray-900 mb-3">
              {t(language, 'fitPage.compareRoutes')}
            </h2>
            <p className="text-base text-gray-600 max-w-2xl mx-auto">
              {t(language, 'fitPage.compareRoutesSub')}
            </p>
          </div>

          {/* Desktop table */}
          <div className="hidden md:block">
            <div className="rounded-lg border border-gray-200 overflow-hidden">
              <table className="w-full text-left">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-4 text-sm font-semibold text-gray-700 w-[22%]">
                      {t(language, 'fitPage.columnPlan')}
                    </th>
                    <th className="px-6 py-4 text-sm font-semibold text-gray-700 w-[28%]">
                      {t(language, 'fitPage.columnBestFor')}
                    </th>
                    <th className="px-6 py-4 text-sm font-semibold text-gray-700">
                      {t(language, 'fitPage.columnKeyValue')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {routes.map((route, index) => {
                    const Icon = route.icon;
                    return (
                      <tr
                        key={route.plan}
                        className={`${index < routes.length - 1 ? 'border-b border-gray-100' : ''} hover:bg-gray-50/60 transition-colors`}
                        data-testid={`fit-row-${route.plan.toLowerCase().replace(/\s+/g, '-')}`}
                      >
                        <td className="px-6 py-6 align-top">
                          <div className="flex items-start space-x-3">
                            <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center flex-shrink-0">
                              <Icon className="w-5 h-5 text-blue-900" />
                            </div>
                            <div className="font-medium text-gray-900 text-base leading-6 pt-1.5">
                              {route.plan}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-6 align-top text-gray-700 text-sm">
                          {t(language, `fitPage.${route.bestForKey}`)}
                        </td>
                        <td className="px-6 py-6 align-top text-gray-600 text-sm leading-relaxed">
                          {t(language, `fitPage.${route.valueKey}`)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Mobile: stacked cards */}
          <div className="md:hidden space-y-4">
            {routes.map((route) => {
              const Icon = route.icon;
              return (
                <Card
                  key={route.plan}
                  className="border-gray-200"
                  data-testid={`fit-card-${route.plan.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <CardContent className="p-6">
                    <div className="flex items-center space-x-3 mb-4">
                      <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Icon className="w-5 h-5 text-blue-900" />
                      </div>
                      <h3 className="text-lg font-medium text-gray-900">{route.plan}</h3>
                    </div>
                    <div className="mb-3">
                      <p className="text-xs uppercase tracking-wide text-gray-500 mb-1">
                        {t(language, 'fitPage.columnBestFor')}
                      </p>
                      <p className="text-sm text-gray-700">{t(language, `fitPage.${route.bestForKey}`)}</p>
                    </div>
                    <div>
                      <p className="text-xs uppercase tracking-wide text-gray-500 mb-1">
                        {t(language, 'fitPage.columnKeyValue')}
                      </p>
                      <p className="text-sm text-gray-600 leading-relaxed">
                        {t(language, `fitPage.${route.valueKey}`)}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* How to choose */}
      <section className="py-20 bg-[#f5f2e8]" data-testid="fit-how-to-choose">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-medium text-gray-900 mb-3">
              {t(language, 'fitPage.howToChoose')}
            </h2>
          </div>

          <div className="space-y-4">
            {choices.map((key) => (
              <div
                key={key}
                className="bg-white rounded-lg border border-gray-200 p-6 flex items-start gap-4 hover:border-blue-900 transition-colors duration-300"
              >
                <div className="flex-shrink-0 mt-0.5">
                  <div className="w-2 h-2 bg-blue-900 rounded-full" />
                </div>
                <div>
                  <p className="text-base text-gray-700 leading-relaxed">
                    {t(language, `fitPage.${key}`)}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-blue-900 text-white" data-testid="fit-final-cta">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-medium mb-5">{t(language, 'fitPage.stillUnsure')}</h2>
          <p className="text-lg text-blue-100 leading-relaxed mb-8">
            {t(language, 'fitPage.stillUnsureDesc')}
          </p>
          <Link to="/start">
            <Button
              size="lg"
              data-testid="fit-final-begin-incorporation-btn"
              className="shine-cta shine-cta-light bg-white text-blue-900 hover:bg-gray-100 px-8"
            >
              {t(language, 'nav.startCta')}
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Fit;
